import { Check, ChevronsUpDown } from "lucide-react";
import { useCallback, useState } from "react";
import { initials } from "@/lib/format";
import { useClickOutside } from "@/lib/useClickOutside";
import { useSession } from "@/lib/session";

/** Indigo square logo mark shown next to the active company name. */
function LogoMark({ name }: { name: string }) {
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-b from-accent to-accent-hover text-label font-bold text-accent-fg">
      {initials(name).charAt(0)}
    </span>
  );
}

/**
 * Workspace switcher at the top of the sidebar. Shows the active company; when
 * the user belongs to more than one company a dropdown lists them all and
 * `switchCompany` re-bootstraps the session on select.
 */
export function CompanySwitcher() {
  const { me, switchCompany } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(open, useCallback(() => setOpen(false), []));

  const active = me?.activeCompany ?? null;
  const companies = me?.companies ?? [];
  const multi = companies.length > 1;

  if (!active) return null;

  async function handleSelect(id: string) {
    setOpen(false);
    if (id === active?.id) return;
    try {
      await switchCompany(id);
    } catch (err) {
      console.error("Failed to switch company", err);
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={!multi}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 rounded-md p-1.5 text-left transition-colors enabled:hover:bg-hairline disabled:cursor-default"
      >
        <LogoMark name={active.name} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body font-semibold text-ink">{active.name}</span>
        </span>
        {multi ? <ChevronsUpDown size={16} strokeWidth={2} className="shrink-0 text-muted" /> : null}
      </button>

      {open && multi ? (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 flex flex-col gap-0.5 rounded-md border border-border bg-bg p-1 shadow-window">
          {companies.map((company) => (
            <button
              key={company.id}
              type="button"
              onClick={() => void handleSelect(company.id)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-hairline"
            >
              <LogoMark name={company.name} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body text-ink">{company.name}</span>
                <span className="block text-meta capitalize text-muted">{company.role}</span>
              </span>
              {company.id === active.id ? (
                <Check size={16} strokeWidth={2.5} className="shrink-0 text-accent" />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
